/**
 * Per-role dashboard resolution.
 *
 * The dashboard is dispatched on a single canonical "dashboard role" derived
 * from the user's Spatie role names. A user may legitimately hold several roles
 * (e.g. an admin who also carries `doctor` for calendar scoping), so the mapping
 * is a strict PRIORITY walk rather than a lookup: the FIRST role in {@link PRIORITY}
 * the user holds wins, and anything unmatched (lab_technician, per-clinic custom
 * roles such as Navident's legacy `operator`) falls back to `generic`.
 *
 * Roles are NOT permissions — variants still gate every widget/fetch through
 * `usePermissions()`. The role only decides WHICH dashboard layout to render.
 */

import { ROLES } from "./roles"

export type DashboardRole =
  | "admin"
  | "team_leader"
  | "coordinator"
  | "accountant"
  | "doctor"
  | "agent"
  | "generic"

/**
 * Highest-authority-first ordering. Deliberately excludes `generic` — that is
 * the fallback, not a matchable role name.
 *
 * 🟥 MUST stay in sync with App\Support\RoleHierarchy::DASHBOARD_PRIORITY
 * (backend/app/Support/RoleHierarchy.php), which also documents the SECOND,
 * deliberately DIFFERENT walk in this codebase:
 * RoleHierarchy::CALENDAR_PRESET_PRECEDENCE, consumed by
 * AuthController::resolveCalendarPresets(). That one is most-specific-FIRST
 * (`doctor` ahead of `admin`) because it picks a calendar lens, not a layout —
 * the "admin who also carries `doctor` for calendar scoping" noted above is
 * exactly the user who needs the two orderings to disagree. Do not unify them.
 */
const PRIORITY = [
  "admin",
  "team_leader",
  "coordinator",
  "accountant",
  "doctor",
  "agent",
] as const

/**
 * Roles that, for now, walk as a canonical role. Radiance 261001: the
 * logistics coordinator starts as the coordinator — the coordinator's board
 * included — until it is given a dashboard of its own.
 *
 * 🟥 MUST match App\Support\RoleHierarchy::STAND_INS, which gives it the
 * coordinator's calendar lens and booking type on the backend.
 */
const STAND_INS: Record<string, DashboardRole> = {
  [ROLES.LOGISTICS_COORDINATOR]: "coordinator",
}

/**
 * Map a user's role-name array onto the dashboard variant that should render.
 *
 * @param roles `user.roles` from the auth store (Spatie role names).
 * @returns the first PRIORITY hit, or `"generic"` when the user holds none of
 *          them (lab_technician, custom clinic roles, or a still-loading user).
 */
export function resolveDashboardRole(
  roles: string[] | undefined | null,
): DashboardRole {
  if (!roles || roles.length === 0) return "generic"

  const held = new Set(roles)
  for (const role of roles) {
    const standsInFor = STAND_INS[role]
    if (standsInFor) held.add(standsInFor)
  }
  for (const role of PRIORITY) {
    if (held.has(role)) return role
  }

  return "generic"
}
